import dayjs from 'dayjs';
import { ICustomersGetResponse, ICustomersUserProfileResponse, IPrescriptionCopayQuantity } from '../endpoints/customers';
import { InvoicesStateEnum } from '../types/enums';
import { IDeliveryItem, IPendingPrescription } from '../types/interfaces';
import { ITestClaimTransferPrescriptions, ITransferPrescriptions } from '../types/interfaces/transfer-prescriptions';
import { ICustomerRequestItem } from '@/shared/types/interfaces/customer-requests';

export const minPrescriptionCount = Number(import.meta.env.VITE_APP_MIN_PRESCRIPTION_COUNT);

export interface IPrescribables {
    includingMedbox: ITestClaimTransferPrescriptions[];
    packagedSeparately: ITestClaimTransferPrescriptions[];
}

export const validInvoicesForCustomerViewing = [
    InvoicesStateEnum.OPEN,
    InvoicesStateEnum.AUTHORIZED,
    InvoicesStateEnum.UNPAID,
    InvoicesStateEnum.DONE
];
export const paidStates = [InvoicesStateEnum.DONE, InvoicesStateEnum.AUTHORIZED];
export const unpaidStates = [InvoicesStateEnum.OPEN, InvoicesStateEnum.UNPAID];
export const isInvoiceValid = (state: InvoicesStateEnum) => validInvoicesForCustomerViewing.includes(state);
export const isInvoicePaid = (state: InvoicesStateEnum) => paidStates.includes(state);
export const isInvoiceUnpaid = (state: InvoicesStateEnum) => unpaidStates.includes(state);
export const getInvoiceNumber = (invoiceNumber: string) => {
    const match = invoiceNumber?.match(/QFE:\/\/(.*)/);
    return match ? match[1] : '';
};

export const getPrescriptionsWithQuantites = (
    prescriptions: ITransferPrescriptions[],
    quantitesMap?: Record<string, IPrescriptionCopayQuantity>
) =>
    prescriptions.map(presc => {
        const fullData = { ...presc } as ITestClaimTransferPrescriptions;
        const quantityInfo = quantitesMap?.[+presc.medid];

        if (fullData && quantityInfo) {
            fullData.copayQuantity = quantityInfo.quantity;
            fullData.copayDaysSupply = quantityInfo.daysSupply;
        }

        return fullData;
    });

export const getPrescriptionWithQuantity = (
    prescription: ITransferPrescriptions,
    quantitesMap?: Record<string, IPrescriptionCopayQuantity>
) => {
    const fullData = { ...prescription } as ITestClaimTransferPrescriptions;
    const quantityInfo = quantitesMap?.[+prescription.medid];

    if (fullData && quantityInfo) {
        fullData.copayQuantity = quantityInfo.quantity;
        fullData.copayDaysSupply = quantityInfo.daysSupply;
    }

    return fullData;
};

export const convertedMedications = (user?: ICustomersGetResponse): IPrescribables => {
    const data: IPrescribables = {
        includingMedbox: [],
        packagedSeparately: []
    };
    user!.transferPrescriptions.forEach(e => {
        const fullData = { ...e } as ITestClaimTransferPrescriptions;
        const quantityInfo = user?.userOnboarding?.details?.prescriptionCopayQuantities?.[+e.medid];

        if (fullData && quantityInfo) {
            fullData.copayQuantity = quantityInfo.quantity;
            fullData.copayDaysSupply = quantityInfo.daysSupply;
        }

        if (e.packaging === 'Include in my MedBox' || !e.packaging) data.includingMedbox.push(fullData);
        if (e.packaging === 'Package separately') data.packagedSeparately.push(fullData);
    });
    return data;
};

export const getUserFullName = (user: ICustomersGetResponse) => `${user.userProfile.firstName} ${user.userProfile.lastName}`;

export const isPendingPrescription = (
    prescription:
        | (ICustomerRequestItem & { details: { prescribable: ITransferPrescriptions } })
        | ITransferPrescriptions
        | IPendingPrescription
): prescription is IPendingPrescription => {
    return (prescription as IPendingPrescription).handled !== undefined;
};

export const isDeliveryItem = (
    prescription: IPendingPrescription | IDeliveryItem | ITransferPrescriptions
): prescription is IDeliveryItem => {
    return (prescription as IDeliveryItem).fill !== undefined;
};

export const checkHasPregnancy = (userProfile?: ICustomersUserProfileResponse) => {
    if (!userProfile || !userProfile.dob || !userProfile.gender) {
        return false;
    }

    if (userProfile.gender !== 'F') {
        return false;
    }
    const birthDate = dayjs(userProfile.dob);
    if (birthDate.add(55, 'years').isBefore(dayjs())) {
        return false;
    }
    return true;
};

export const isUnderForty = (dob: string) => {
    const birthDate = dayjs(dob);
    return birthDate.add(40, 'years').isAfter(dayjs());
};

export const allUnhandledPendingPrescriptions = (patPendingPrescriptions: IPendingPrescription[] | null) =>
    (patPendingPrescriptions || []).filter(pendingRx => !pendingRx.handled);

export const medicationsForceRx = (medicationName: string): boolean => {
    return ['famotidine 20 mg'].some(med => medicationName.toLowerCase().includes(med));
};

export const isEnoughMedicationsToPassOnboarding = (customer: ICustomersGetResponse, transferPrescriptions?: ITransferPrescriptions[]) => {
    const prescriptionList = transferPrescriptions || customer.transferPrescriptions || [];
    const prescriptionCount =
        prescriptionList?.filter(item => !item.prescribable.otc || medicationsForceRx(item.prescribable.name))?.length || 0;
    return prescriptionCount >= minPrescriptionCount;
};
